// #region imports

// --- Angular
import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// --- 3rd Party
import { TranslateService } from '@ngx-translate/core';
import { JL } from 'jsnlog';

// --- HS Code
import { GroupModel, RespondentModel, UndoModel } from '../../../models';
import { RemoteCommandOfT } from './remote-command';
import { DeleteGroupCommand } from './delete-group-command';
import { AppService } from '../app.service';
import { ParticipantGroupsBaseService } from '../survey/participant-groups-base.service';
import { DeleteRespondentCommand } from './delete-respondent-command';
import { BehaviorSubject, Observable } from 'rxjs';
import { MoveRespondentCommand } from './move-respondent-command';

@Injectable({
  providedIn: 'root'
})
export class RemoteCommandService {

  protected _undoRespondentCommand$: BehaviorSubject<UndoModel | null> = new BehaviorSubject<UndoModel | null>(null);
  get undoRespondentCommand$(): Observable<UndoModel | null> {
    return this._undoRespondentCommand$.asObservable();
  }

  private readonly _remoteCommands: RemoteCommandOfT<GroupModel[]>[] = [];

  constructor(
    @Inject('JSNLOG') private _logger: JL.JSNLog,
    private _appService: AppService,
    private _httpClient: HttpClient,
    private _participantGroupsService: ParticipantGroupsBaseService,
    private _translateService: TranslateService
  ) { }

  deleteParticipantGroup(groups: GroupModel[], group: GroupModel): void {
    const command = new DeleteGroupCommand(this._logger, groups, group, this._appService, this._httpClient, this._translateService);
    this._remoteCommands.push(command);
    let subs = command.commandResult$.subscribe(result => {
      this._participantGroupsService.refreshGroups(result);
      subs.unsubscribe();
      if (group.isSet) {
        const undo = {
          text: this._translateService.instant('v2GroupDeleteUndotext', {groupName: group.groupName})
        } as UndoModel;
        this._undoRespondentCommand$.next(undo);
      }
    });
    command.execute();
  }

  deleteRespondent(groups: GroupModel[], respondent: RespondentModel): void {
    const command = new DeleteRespondentCommand(this._logger, groups, respondent, this._appService, this._httpClient, this._translateService);
    this._remoteCommands.push(command);
    let subs = command.commandResult$.subscribe(result => {
      if (result.length === 0) {
        this._participantGroupsService.loadGroups();
      } else {
        this._participantGroupsService.refreshGroups(result);
      }
      subs.unsubscribe();
      const undo = {
        text: this._translateService.instant('v2RespondentDeleteUndotext', { firstName: respondent.firstName, lastName: respondent.lastName})
      } as UndoModel;
      this._undoRespondentCommand$.next(undo);
    });

    command.execute();
  }

  moveRespondent(groups: GroupModel[], respondent: RespondentModel, targetGroup: GroupModel): void {
    const command = new MoveRespondentCommand(this._logger, groups, respondent, targetGroup, this._appService, this._httpClient, this._translateService);
    this._remoteCommands.push(command);
    let subs = command.commandResult$.subscribe(result => {
      if (result.length === 0) {
        this._participantGroupsService.loadGroups();
      } else {
        this._participantGroupsService.refreshGroups(result);
      }
      subs.unsubscribe();
      const undo = {
        text: this._translateService.instant('v2RespondentMoveUndotext', { firstName: respondent.firstName, lastName: respondent.lastName, newGroupName: targetGroup.groupName})
      } as UndoModel;
      this._undoRespondentCommand$.next(undo);
    });
    command.execute();
  }

  undo(): void {
    const command = this._remoteCommands.pop();
    if (command) {
      let subs = command.commandResult$.subscribe(result => {
        this._participantGroupsService.refreshGroups(result);
        subs.unsubscribe();
      });
      command.undo();
    }
  }

  clearUndo(): void {
    this._remoteCommands.splice(0, this._remoteCommands.length);
    this._undoRespondentCommand$.next(null);
  }
}
